import { usePrevious } from '@sequencehq/utils'
import { useAggregatedCreditNote } from 'CreditNotes/hooks/useAggregatedCreditNote'
import { useClientCopy } from 'hooks/useClientCopy'
import { AggregatedCreditNote, LineItem } from 'CreditNotes/types'
import { recalculateCreditNote } from 'CreditNotes/utils/calculation'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSaveAggregatedCreditNote } from './useSaveAggregatedCreditNote'
import { dequal } from 'dequal'

export const useCreditNoteEditing = (creditNoteId: string) => {
  const { creditNote: serverCreditNote } = useAggregatedCreditNote(creditNoteId)
  const [serverRequestTime, setServerRequestTime] = useState(0)

  const [clientCreditNote, setClientCreditNote, clientUpdatedTime] =
    useClientCopy(serverCreditNote, serverRequestTime)

  const creditNote = useMaintainUnselectedLineItems(clientCreditNote)
  const saveCreditNote = useSaveAggregatedCreditNote(creditNoteId)

  const previousCreditNote = usePrevious(creditNote)

  useEffect(() => {
    if (!clientUpdatedTime) {
      return
    }

    if (dequal(creditNote, previousCreditNote)) {
      return
    }

    if (!serverCreditNote || !creditNote) {
      return
    }

    saveCreditNote(serverCreditNote, creditNote)
    setServerRequestTime(Date.now())
  }, [
    serverCreditNote,
    creditNote,
    saveCreditNote,
    previousCreditNote,
    clientUpdatedTime
  ])

  const updateCreditNote = useCallback(
    (newCreditNote: AggregatedCreditNote) => {
      setClientCreditNote(recalculateCreditNote(newCreditNote))
    },
    [setClientCreditNote]
  )

  const updateLineItems = useCallback(
    (lineItemsToUpdate: LineItem[]) => {
      if (!creditNote) {
        return
      }

      updateCreditNote({
        ...creditNote,
        lineItemGroups: creditNote.lineItemGroups.map(lineItemGroup => {
          if (
            !lineItemsToUpdate.find(item => item.groupId === lineItemGroup.id)
          ) {
            return lineItemGroup
          }
          return {
            ...lineItemGroup,
            lineItems: lineItemGroup.lineItems.map(lineItem => {
              return (
                lineItemsToUpdate.find(item => item.id === lineItem.id) ??
                lineItem
              )
            })
          }
        })
      })
    },
    [creditNote, updateCreditNote]
  )

  const updateBillingPeriod = useCallback(
    ({
      billingPeriodStart,
      billingPeriodEnd
    }: {
      billingPeriodStart: AggregatedCreditNote['billingPeriodStart']
      billingPeriodEnd: AggregatedCreditNote['billingPeriodEnd']
    }) => {
      if (!creditNote) {
        return
      }

      updateCreditNote({
        ...creditNote,
        billingPeriodStart,
        billingPeriodEnd
      })
    },
    [creditNote, updateCreditNote]
  )

  const updateAccountingDate = useCallback(
    (accountingDate: AggregatedCreditNote['accountingDate']) => {
      if (!creditNote) {
        return
      }

      updateCreditNote({
        ...creditNote,
        accountingDate
      })
    },
    [creditNote, updateCreditNote]
  )

  const updateTaxDate = useCallback(
    (taxDate: AggregatedCreditNote['taxDate']) => {
      if (!creditNote) {
        return
      }

      updateCreditNote({
        ...creditNote,
        taxDate
      })
    },
    [creditNote, updateCreditNote]
  )

  return {
    creditNote,
    updateLineItems,
    updateBillingPeriod,
    updateAccountingDate,
    updateTaxDate
  }
}

// For the purposes of our first iteration,
// The server has no concept of whether a line item is included or not
// i.e. when a line item is unselected, we'll delete it from the server
//
// To be able to show the unselected items even after they're deleted from the server,
// we need to keep the initial server response
const useMaintainUnselectedLineItems = (
  creditNote: AggregatedCreditNote | null
) => {
  const flattenedLineItems = useMemo(
    () => creditNote?.lineItemGroups.flatMap(group => group.lineItems) ?? [],
    [creditNote?.lineItemGroups]
  )

  const [allLineItems, setAllLineItems] = useState(flattenedLineItems)
  const previousFlattenedLineItems = usePrevious(flattenedLineItems)

  useEffect(() => {
    if (dequal(flattenedLineItems, previousFlattenedLineItems)) {
      return
    }

    // Refresh each stored item from the new server payload by ID. Drop
    // stored items that the server no longer returns *unless* the user has
    // explicitly deselected them — those need to stay visible so the user
    // can toggle them back on. This prevents stale items accumulating when
    // the server regenerates line items with new IDs (e.g. recalculation of
    // an IN_PROGRESS credit note).
    const refreshed = allLineItems.reduce<LineItem[]>((acc, stored) => {
      const current = flattenedLineItems.find(item => item.id === stored.id)
      if (current) return [...acc, current]
      if (stored.selected === false) return [...acc, stored]
      return acc
    }, [])

    const refreshedIds = new Set(refreshed.map(item => item.id))
    const newItems = flattenedLineItems.filter(
      item => !refreshedIds.has(item.id)
    )

    setAllLineItems([...refreshed, ...newItems])
  }, [flattenedLineItems, allLineItems, previousFlattenedLineItems])

  return useMemo(() => {
    if (!creditNote) {
      return null
    }
    return maintainUnselectedLineItems(creditNote, allLineItems)
  }, [creditNote, allLineItems])
}

function maintainUnselectedLineItems(
  creditNote: AggregatedCreditNote,
  allLineItems: LineItem[]
): AggregatedCreditNote {
  return {
    ...creditNote,
    lineItemGroups: creditNote.lineItemGroups.map(group => {
      const matchedGroupLineItemIds = new Set<string>()

      return {
        ...group,
        lineItems: allLineItems
          .filter(item => item.groupId === group.id)
          .map(storedLineItem => {
            // Match by ID first, then fall back to title+rate for items that
            // were deleted and re-created with a new server ID. Each
            // group.lineItems entry can only be claimed once to prevent
            // multiple stored items with the same title+rate from all
            // resolving to the same entry.
            const currentLineItem =
              group.lineItems.find(
                item =>
                  item.id === storedLineItem.id &&
                  !matchedGroupLineItemIds.has(item.id)
              ) ??
              group.lineItems.find(
                item =>
                  !matchedGroupLineItemIds.has(item.id) &&
                  item.title === storedLineItem.title &&
                  item.rate === storedLineItem.rate
              )

            if (currentLineItem) {
              matchedGroupLineItemIds.add(currentLineItem.id)
            }

            if (currentLineItem?.deleted || storedLineItem.deleted) {
              return null
            }
            if (currentLineItem) {
              return {
                ...currentLineItem,
                selected: currentLineItem.selected ?? true
              }
            }

            return { ...storedLineItem, selected: false }
          })
          .filter(Boolean) as LineItem[]
      }
    })
  }
}
